// Shared page-scroll lock (menu drawer + bottom-slide modals can be open at the same time):
// counted so closing one layer does not free the scroll while another is still open.
// Locks native overflow on html + body (iOS needs both) and pauses Lenis (custom--smooth-scroll).
window.customScrollLock = window.customScrollLock || {
  count: 0,
  lock() {
    if (++this.count === 1) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.dispatchEvent(new Event("lenis:stop"));
    }
  },
  unlock() {
    if (this.count > 0 && --this.count === 0) {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.dispatchEvent(new Event("lenis:start"));
    }
  },
};

// @ts-nocheck
if (!customElements.get("header-menu")) {
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      // Set initial colors on root and disable transition to prevent flash
      const root = document.documentElement;

      // Check if URL contains "/policies/" - apply white header with blue text
      const isPoliciesPage = window.location.pathname.includes("/policies/");

      root.style.transition = "none";

      if (isPoliciesPage) {
        // Apply white background with blue text for policies pages
        root.style.setProperty(
          "--custom-nav-bg-color",
          "var(--custom-color-white)"
        );
        root.style.setProperty(
          "--custom-nav-text-color",
          "var(--custom-color-blue)"
        );
        root.style.setProperty("--custom-nav-logo-light-display", "none");
        root.style.setProperty("--custom-nav-logo-dark-display", "block");
        root.style.setProperty("--header-logo-mobile-opacity", "0");

        // Add spacing to push content down
        document.body.style.paddingTop = "96px";
      } else if (window.scrollY === 0) {
        root.style.setProperty("--custom-nav-bg-color", "transparent");
        root.style.setProperty("--custom-nav-text-color", "#ffffff");
        root.style.setProperty("--custom-nav-logo-light-display", "block");
        root.style.setProperty("--custom-nav-logo-dark-display", "none");
        root.style.setProperty("--header-logo-mobile-opacity", "1");
      }

      queueMicrotask(() => {
        this.init();
      });
    }

    init() {
      this.drawer = this.querySelector("[data-menu-drawer]");
      this.overlay = this.querySelector("[data-menu-overlay]");
      this.overlayCanvas = this.querySelector("[data-menu-overlay-canvas]");
      this.overlayKey = "";
      if (this.overlayCanvas) {
        window.addEventListener("resize", () => {
          if (this.isOpen) this.renderOverlay();
        });
        // Paint once while idle so the first open does not wait on the pixel loop.
        (window.requestIdleCallback || ((cb) => setTimeout(cb, 300)))(() => this.renderOverlay());
      }
      this.trigger = this.querySelector("[data-menu-trigger]");
      this.closeBtn = this.querySelector("[data-menu-close]");
      this.header = this.closest("header") || document.querySelector("header");
      this.isOpen = false;

      if (this.trigger) {
        this.trigger.addEventListener("click", (e) => {
          e.preventDefault();
          this.open();
        });
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.close();
        });
      }

      if (this.overlay) {
        this.overlay.addEventListener("click", () => {
          this.close();
        });
      }

      // Close on Escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.close();
        }
      });

      // Focus trap
      if (this.drawer) {
        this.drawer.addEventListener("keydown", (e) => {
          if (e.key === "Tab" && this.isOpen) {
            this.handleFocusTrap(e);
          }
        });
      }

      // Set initial header colors based on scroll position
      this.updateHeaderColors();

      // Re-enable transition after initial setup
      document.documentElement.style.transition = "";
      if (this.header) {
        this.header.style.transition = "";
      }

      // Scroll listener for header styling
      window.addEventListener("scroll", () => {
        this.updateHeaderColors();
      });

      // Hide on scroll down / show on scroll up (phones + portrait tablets only, and never
      // while the menu drawer is open). Opt-in via data-hide-on-scroll on the <header>.
      if (this.header && this.header.hasAttribute("data-hide-on-scroll")) {
        const mq = window.matchMedia(
          "(max-width: 749px), (min-width: 750px) and (max-width: 1024px) and (orientation: portrait)"
        );
        let lastY = window.scrollY;
        let ticking = false;
        const update = () => {
          ticking = false;
          const y = window.scrollY;
          const delta = y - lastY;
          const drawerOpen = this.drawer && this.drawer.classList.contains("custom--active");
          if (!mq.matches || drawerOpen || y <= 8) {
            this.header.classList.remove("custom--header--hidden");
          } else if (delta > 6 && y > 120) {
            this.header.classList.add("custom--header--hidden");
          } else if (delta < -6) {
            this.header.classList.remove("custom--header--hidden");
          }
          lastY = y;
        };
        window.addEventListener("scroll", () => {
          if (!ticking) { ticking = true; requestAnimationFrame(update); }
        }, { passive: true });
        mq.addEventListener && mq.addEventListener("change", update);
      }
    }

    // Sections can mark themselves with data-header-zone="light" (transparent nav, white text)
    // or data-header-zone="dark" (transparent nav, blue text). The zone sitting under the nav
    // wins; the last matching element in DOM order is treated as the top-most. Legacy:
    // [data-header-transparent-until] behaves like zone="light".
    getHeaderZone() {
      const headerHeight = this.header ? this.header.offsetHeight : 0;
      const probeY = Math.max(1, headerHeight - 1);
      const zones = document.querySelectorAll("[data-header-zone], [data-header-transparent-until]");
      if (!zones.length) return window.scrollY > 0 ? "solid" : "light";
      let zone = "solid";
      // Phones + portrait tablets may ask for a different zone (data-header-zone-mobile), e.g. a hero that
      // is a light photo on desktop but a dark video on phones.
      const small = window.matchMedia(
        "(max-width: 749px), (min-width: 750px) and (max-width: 1024px) and (orientation: portrait)"
      ).matches;
      const phone = window.matchMedia("(max-width: 749px)").matches;
      zones.forEach((el) => {
        // data-header-zone-scroll="top": the zone only applies while the page is at the very top
        if (el.getAttribute("data-header-zone-scroll") === "top" && window.scrollY > 8) return;
        const r = el.getBoundingClientRect();
        if (r.top <= probeY && r.bottom > probeY) {
          // data-header-zone-mobile-scope="phones": the phone zone stops at 749px — a split hero keeps
          // its copy beside the footage on portrait tablets, so the desktop zone is the right one there
          const phonesOnly = el.getAttribute("data-header-zone-mobile-scope") === "phones";
          const mobileZone = small && (phone || !phonesOnly) ? el.getAttribute("data-header-zone-mobile") : null;
          zone = mobileZone || el.getAttribute("data-header-zone") || "light";
        }
      });
      return zone;
    }

    updateHeaderColors() {
      const root = document.documentElement;
      const isPoliciesPage = window.location.pathname.includes("/policies/");

      // For policies pages, always use white background with blue text
      if (isPoliciesPage) {
        root.style.setProperty(
          "--custom-nav-bg-color",
          "var(--custom-color-white)"
        );
        root.style.setProperty(
          "--custom-nav-text-color",
          "var(--custom-color-blue)"
        );
        root.style.setProperty("--custom-nav-logo-light-display", "none");
        root.style.setProperty("--custom-nav-logo-dark-display", "block");
        root.style.setProperty("--header-logo-mobile-opacity", "0");
      } else if (this.getHeaderZone() === "solid") {
        // For other pages, change to white on scroll
        root.dataset.navZone = "solid";
        root.style.setProperty(
          "--custom-nav-bg-color",
          "var(--custom-color-white)"
        );
        root.style.setProperty(
          "--custom-nav-text-color",
          "var(--custom-color-blue)"
        );
        root.style.setProperty("--custom-nav-logo-light-display", "none");
        root.style.setProperty("--custom-nav-logo-dark-display", "block");
        root.style.setProperty("--header-logo-mobile-opacity", "0");
      } else if (this.getHeaderZone() === "dark") {
        // Transparent nav over a light section: blue text and logo
        root.dataset.navZone = "dark";
        root.style.setProperty("--custom-nav-bg-color", "transparent");
        root.style.setProperty(
          "--custom-nav-text-color",
          "var(--custom-color-blue)"
        );
        root.style.setProperty("--custom-nav-logo-light-display", "none");
        root.style.setProperty("--custom-nav-logo-dark-display", "block");
        root.style.setProperty("--header-logo-mobile-opacity", "0");
      } else {
        root.dataset.navZone = "light";
        // Default transparent header at top
        root.style.setProperty("--custom-nav-bg-color", "transparent");
        root.style.setProperty("--custom-nav-text-color", "#ffffff");
        root.style.setProperty("--custom-nav-logo-light-display", "block");
        root.style.setProperty("--custom-nav-logo-dark-display", "none");
        root.style.setProperty("--header-logo-mobile-opacity", "1");
      }
    }

    getFocusableElements() {
      if (!this.drawer) return [];
      const focusableSelectors =
        "a[href], button:not([disabled]), textarea, input, select";
      return Array.from(this.drawer.querySelectorAll(focusableSelectors));
    }

    handleFocusTrap(e) {
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    open() {
      this.isOpen = true;

      // Store the element that triggered the menu
      this.previouslyFocusedElement = document.activeElement;

      if (this.drawer) {
        this.drawer.classList.add("custom--active");
        this.drawer.setAttribute("aria-hidden", "false");
      }

      if (this.overlay) {
        this.renderOverlay();
        this.overlay.classList.add("custom--active");
        this.overlay.setAttribute("aria-hidden", "false");
      }

      if (this.trigger) {
        this.trigger.setAttribute("aria-expanded", "true");
        // Add data attribute to help with CSS targeting
        this.trigger.setAttribute("data-drawer-open", "true");
      }

      this.lockScroll();

      // Move focus to first focusable element in drawer
      setTimeout(() => {
        const focusableElements = this.getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }, 50);
    }

    // Radial page overlay, painted on a canvas so the alpha ramp can be dithered per pixel.
    // A CSS radial-gradient quantises each stop to 8-bit alpha, and a 100% -> 60% ramp across a
    // 1500px viewport leaves ~100 levels over ~1500px: visible bands. Here the alpha is computed
    // in float per pixel, noise in [-0.5, 0.5) is added and THEN it is rounded, so the rounding
    // error is spread across neighbouring pixels instead of lining up into steps.
    renderOverlay() {
      const canvas = this.overlayCanvas;
      if (!canvas || !this.overlay) return;
      // Flat overlay on small screens (CSS handles it): skip the per-pixel ramp
      if (
        this.overlay.classList.contains("custom--overlay--mobile-solid") &&
        window.matchMedia("(max-width: 749px), (min-width: 750px) and (max-width: 1024px) and (orientation: portrait)").matches
      ) return;
      const cs = getComputedStyle(this.overlay);
      const pct = (name, fallback) => {
        const v = parseFloat(cs.getPropertyValue(name));
        return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) / 100 : fallback;
      };
      const color = cs.getPropertyValue("--drawer-overlay-color").trim() || "#003466";
      const a0 = pct("--drawer-overlay-opacity", 1); // at the lower-left corner
      const a1 = pct("--drawer-overlay-edge", 0.6); // at the far (top-right) corner
      const hold = Math.min(0.95, pct("--drawer-overlay-hold", 0.35)); // share of the radius kept at a0
      // Draw at the real pixel ratio: an upscaled 1x canvas is bilinear-filtered by the compositor, which
      // averages neighbouring dithered pixels back towards the plain step and the bands reappear.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(window.innerWidth * dpr));
      const h = Math.max(1, Math.round(window.innerHeight * dpr));
      const key = [w, h, color, a0, a1, hold].join("|");
      if (key === this.overlayKey) return;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      canvas.width = w;
      canvas.height = h;

      // Resolve the colour setting (hex or rgb()) to bytes via the canvas itself.
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 1, 1);
      const px = ctx.getImageData(0, 0, 1, 1).data;
      const cr = px[0], cg = px[1], cb = px[2];

      const img = ctx.createImageData(w, h);
      const d = img.data;
      const invR = 1 / Math.sqrt(w * w + h * h); // farthest corner from (0, h), as radial-gradient does
      const span = a0 - a1;
      let i = 0;
      for (let y = 0; y < h; y++) {
        const dy2 = (h - y) * (h - y);
        for (let x = 0; x < w; x++) {
          let t = (Math.sqrt(x * x + dy2) * invR - hold) / (1 - hold);
          if (t < 0) t = 0;
          else if (t > 1) t = 1;
          t = t * t * (3 - 2 * t); // smoothstep, same ease as the CSS stops
          // Two interleaved-gradient-noise samples (Jimenez) summed = triangular (TPDF) dither in
          // (-1, 1) LSB: the standard for hiding quantisation steps without visible noise pattern.
          const n1 = (52.9829189 * ((0.06711056 * x + 0.00583715 * y) % 1)) % 1;
          const n2 = (52.9829189 * ((0.06711056 * (x + 37) + 0.00583715 * (y + 91)) % 1)) % 1;
          const a = (a0 - span * t) * 255 + (n1 + n2 - 1);
          d[i] = cr;
          d[i + 1] = cg;
          d[i + 2] = cb;
          d[i + 3] = a < 0 ? 0 : a > 255 ? 255 : Math.round(a);
          i += 4;
        }
      }
      ctx.putImageData(img, 0, 0);
      this.overlayKey = key;
      this.overlay.classList.add("custom--overlay--drawn");
    }

    lockScroll() {
      window.customScrollLock ? window.customScrollLock.lock() : (document.body.style.overflow = "hidden");
    }
    unlockScroll() {
      window.customScrollLock ? window.customScrollLock.unlock() : (document.body.style.overflow = "");
    }

    close() {
      this.isOpen = false;

      if (this.drawer) {
        this.drawer.classList.remove("custom--active");
        this.drawer.setAttribute("aria-hidden", "true");
      }

      if (this.overlay) {
        this.overlay.classList.remove("custom--active");
        this.overlay.setAttribute("aria-hidden", "true");
      }

      if (this.trigger) {
        this.trigger.setAttribute("aria-expanded", "false");
        // Remove data attribute to restore hamburger menu visibility
        this.trigger.removeAttribute("data-drawer-open");
      }

      this.unlockScroll();

      // Return focus to trigger button with a slight delay to ensure smooth transition
      if (this.previouslyFocusedElement) {
        setTimeout(() => {
          this.previouslyFocusedElement.focus();
        }, 100);
      }
    }
  }

  customElements.define("header-menu", HeaderMenu);
}
